import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum, Contract_Status_Enum } from '@kheops/graphql';
import ContractRequestButton from '../ContractRequestButton';
import ResumeNegotiationButton from '../ResumeNegotiationButton';
import { useJoinOrCreateChat } from '../../hooks/useJoinOrCreateChat';
import { currentContextAtom } from '../../state';
import { RoutePaths } from '../../routes/AppRoutes';
import { ContractPreview } from '../../hooks/useContracts';
import useOpenable from '../../hooks/useOpenable';
import BuyerInfoDialog from '../buyer/dialog/BuyerInfoDialog';
import { useGetContactBoxCompanyInfoQuery } from '../../queries/__generated__/getContactBoxCompanyInfo.generated';
import CompanyContactDialog from './CompanyContactDialog';

export interface ContactBoxProps {
  contactCompanyId: string;
  contract?: ContractPreview;
  actionButtonDisplay?: 'icon' | 'text' | 'short';
  buttonSize?: 'medium' | 'large' | 'small';
}

export default function ContactBox({ contactCompanyId, contract, actionButtonDisplay = 'text', buttonSize }: ContactBoxProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts']);
  const { realm } = useAtomValue(currentContextAtom);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: companyInfoData } = useGetContactBoxCompanyInfoQuery({ variables: { id: contactCompanyId } });
  const navigate = useNavigate();
  const { channelUrl, joinOrCreateChat, loading: isOpeningContractChat } = useJoinOrCreateChat();
  const size = useMemo(() => {
    if (!buttonSize) {
      return isMobile ? 'medium' : 'large';
    }

    return buttonSize;
  }, [isMobile, buttonSize]);

  const contractRequestButtonContent = useMemo((): string | React.JSX.Element => {
    switch (actionButtonDisplay) {
      case 'text':
        return t('contracts:contact_now');
      case 'short':
        return t('common:contact');
      case 'icon':
        return <MessageOutlinedIcon />;
    }
  }, [actionButtonDisplay]);

  const { isOpen: isContactDialogOpen, open: openContactDialog, close: closeContactDialog } = useOpenable();
  const { isOpen: isBuyerInfoDialogOpen, open: openBuyerInfoDialog, close: closeBuyerInfoDialog } = useOpenable();
  const isContractSigned = contract?.status === Contract_Status_Enum.Signed;

  const company = useMemo(() => {
    return companyInfoData?.company_by_pk;
  }, [companyInfoData]);

  useEffect((): void => {
    if (channelUrl) {
      navigate(`${RoutePaths.CHAT_LIST}?channelUrl=${channelUrl}`);
    }

  }, [channelUrl]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: {
          xs: 'row',
        },
        gap: {
          xs: 1,
        },
      }}
    >
      {company && (
        <>
          {isContractSigned && (
            <Button
              loading={isOpeningContractChat}
              onClick={() => joinOrCreateChat(contract!)}
            >
              {actionButtonDisplay !== 'icon' ? t('common:send_a_message') : <MessageOutlinedIcon />}
            </Button>
          )}
          {!!contract && (
            <>
              {isContractSigned && company.business_profile === Business_Profile_Enum.Buyer && (
                <>
                  <Button onClick={openBuyerInfoDialog}>
                    {actionButtonDisplay !== 'icon' ? t('common:more_information') : <InfoOutlinedIcon />}
                  </Button>
                  <BuyerInfoDialog close={closeBuyerInfoDialog} isOpen={isBuyerInfoDialogOpen} companyId={company.id} />
                </>
              )}
              {company.business_profile === Business_Profile_Enum.Supplier && (
                <>
                  {!isContractSigned && (
                    <Button
                      variant="outlined"
                      startIcon={actionButtonDisplay !== 'icon' ? <PersonOutlineIcon /> : undefined}
                      onClick={() => openContactDialog()}
                    >
                      {actionButtonDisplay !== 'icon' ? t('common:contacts') : <PersonOutlineIcon />}
                    </Button>
                  )}
                  <CompanyContactDialog
                    company={company}
                    isOpen={isContactDialogOpen}
                    onClose={closeContactDialog}
                    contractRequestButton={
                      isContractSigned
                        ? undefined
                        : (
                          <ResumeNegotiationButton contract={contract} />
                        )
                    }
                  />
                </>
              )}
            </>
          )}

          {contract && !isContractSigned && realm === Business_Profile_Enum.Buyer && (
            <ResumeNegotiationButton
              contract={contract}
              display={actionButtonDisplay === 'icon' ? 'icon' : 'text'}
            />
          )}

          {!contract && realm === Business_Profile_Enum.Buyer && (
            <ContractRequestButton
              companyId={company.id}
              buttonContent={contractRequestButtonContent}
              buttonProps={{
                size,
                sx: {
                  width: 'fit-content',
                },
              }}
            />
          )}
        </>
      )}
    </Box>
  );
}
